import { Link } from 'react-router-dom';
import { localised, useI18n } from './i18n';
import { objectiveTabs, tabLabel, type TabObjective } from './tabs';

/**
 * The bar of plans that sits above the plan, the progress and the conversation. F-151 step 8.
 *
 * **Nothing at all with one plan**, which is every learner today — see `objectiveTabs`, where
 * that rule lives and is tested. This component renders `null` rather than an empty element, so
 * a screen with one plan has exactly the markup it had before this step and none of its
 * spacing changes.
 *
 * **Links and not an ARIA `tablist`.** The pattern looks like tabs and behaves like
 * navigation: each one is an address (`?o=`), it goes in the browser's history, it can be
 * shared, and the middle-click a laptop user expects works. `role="tablist"` would promise a
 * keyboard contract this does not implement — arrow keys moving between tabs, one tab stop for
 * the set — and would tell a screen reader that a panel is about to change on the same page
 * when in fact the page is about to be navigated. A `nav` of links describes what actually
 * happens.
 *
 * The logic is all in `tabs.ts` for the reason that file gives: `npm test` does not type-check
 * `.tsx`, so anything decided in here is decided where no test can see it.
 */
export default function ObjectiveTabs({
  objectives,
  selected,
  path,
}: {
  /** Every live plan, as `GET /api/objectives/current` answers. `undefined` until it has. */
  objectives: readonly TabObjective[] | undefined;
  /** The plan the screen is showing. */
  selected: string | undefined;
  /** The screen the tabs stay on, from `ROUTES`. */
  path: string;
}) {
  const { t, locale } = useI18n();

  const tabs = objectiveTabs({
    objectives,
    selected,
    path,
    labelOf: (objective, index) =>
      tabLabel({
        /*
         * Resolved here rather than in `tabs.ts` because `localised` needs the reader's locale
         * and that module stays React-free so its rules can be tested. An empty string when
         * there is no title yet, which is exactly what `tabLabel` treats as "no title".
         */
        title: objective.title ? localised(objective.title, locale, '') : '',
        description: objective.description,
        fallback: t('tabs.plan'),
        index,
      }),
  });

  if (tabs.length === 0) return null;

  return (
    <nav className="objective-tabs" aria-label={t('tabs.label')}>
      <ul>
        {tabs.map((tab) => (
          <li key={tab.objectiveId}>
            <Link
              to={tab.to}
              className={tab.selected ? 'objective-tab on' : 'objective-tab'}
              /*
               * `page` and not `true`: this is the plan whose page is on screen, and the two
               * spellings read differently in a screen reader. The selected tab stays a link so
               * that tapping it is a reload of the screen rather than a dead control.
               */
              {...(tab.selected ? { 'aria-current': 'page' as const } : {})}
            >
              {tab.label}
              {/*
               * How many messages are waiting in that plan's chat, and **absent is not nought**
               * (see `TabObjective.unread`). The number is the plan's own, counted against the
               * plan's own marker — which is what step 8 rewrote `markSeen` to make possible,
               * and the reason this can be shown at all without lying.
               */}
              {tab.unread !== undefined && tab.unread > 0 && (
                /*
                 * The number is read out, not hidden. The first draft put `aria-hidden` on the
                 * digits and the word in the `sr-only` span, which announced "Ayurveda,
                 * unread" — the one thing a count is for, missing. It is one span now: the
                 * visible digits, and the word after them for anybody who cannot see that the
                 * pill is an unread count.
                 */
                <span className="objective-tab-unread">
                  {tab.unread}
                  <span className="sr-only">{` ${t('tabs.unread')}`}</span>
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
